import React, { useState, useCallback, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  CheckCircle as PostIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import transferService from '../../../../services/transferService';
import venueLocationService from '../../../../services/venueLocationService';
import type { Transfer, TransferLine } from '../../../../types/venue';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatBottleSize = (ml: number | null): string => {
  if (!ml) return '';
  return ml >= 1000 ? `${ml / 1000}L` : `${ml}ml`;
};

// ── Component ─────────────────────────────────────────────────────────────────

const TransferReview: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { venueId, venueName } = useVenue();
  const { transferId } = useParams<{ transferId: string }>();
  const entryPath = `/admin/venues/${venueId}/transfers/${transferId}`;
  const listPath = `/admin/venues/${venueId}/transfers`;

  const [transfer, setTransfer] = useState<Transfer | null>(null);
  const [lines, setLines] = useState<TransferLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  // ── Resolved location names (independent of line snapshots) ──────────────
  const [fromLocationName, setFromLocationName] = useState<string | null>(null);
  const [toLocationName, setToLocationName] = useState<string | null>(null);

  const [step, setStep] = useState<'review' | 'posting' | 'success' | 'error'>('review');
  const [postError, setPostError] = useState<string | null>(null);
  const [movementsPosted, setMovementsPosted] = useState(0);

  const loadTransfer = useCallback(async () => {
    if (!venueId || !transferId) return;
    setLoading(true);
    setLoadError(null);
    try {
      const { transfer: t, lines: l } = await transferService.getTransfer(venueId, transferId);
      if (t.status === 'posted') {
        navigate(entryPath, { replace: true });
        return;
      }
      setTransfer(t);
      setLines(l);
    } catch (err: any) {
      setLoadError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to load transfer');
    } finally {
      setLoading(false);
    }
  }, [venueId, transferId, entryPath, navigate]);

  useEffect(() => { loadTransfer(); }, [loadTransfer]);

  // ── Resolve location names on mount (non-blocking, non-fatal) ────────────
  useEffect(() => {
    if (!transfer || !venueId) return;
    if (transfer.fromLocationId) {
      venueLocationService.getLocation(venueId, transfer.fromLocationId)
        .then(loc => setFromLocationName(loc.name))
        .catch(() => {});
    }
    if (transfer.toLocationId) {
      venueLocationService.getLocation(venueId, transfer.toLocationId)
        .then(loc => setToLocationName(loc.name))
        .catch(() => {});
    }
  }, [transfer?.fromLocationId, transfer?.toLocationId, venueId]);

  const handlePost = async () => {
    if (!venueId || !transferId) return;
    setStep('posting');
    setPostError(null);
    try {
      const result = await transferService.postTransfer(venueId, transferId);
      setMovementsPosted(result.movementsPosted);
      setStep('success');
      window.scrollTo(0, 0);
    } catch (err: any) {
      setPostError(err?.response?.data?.error?.message ?? err?.message ?? 'Posting failed');
      setStep('error');
    }
  };

  // Resolved from venue locations API — never falls back to raw ObjectIds.
  // Shows '…' while the async fetch is in flight.
  const locationLabel = (id: string | null | undefined): string => {
    if (!id) return `${venueName} (General)`;
    if (id === transfer?.fromLocationId) return fromLocationName ?? '…';
    if (id === transfer?.toLocationId) return toLocationName ?? '…';
    return '…';
  };

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', pt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={{ p: 3 }}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(entryPath)} sx={{ mb: 2, textTransform: 'none', color: 'text.secondary' }}>
          Back to Transfer
        </Button>
        <Alert severity="error">{loadError}</Alert>
      </Box>
    );
  }

  // ── Posting spinner ───────────────────────────────────────────────────────
  if (step === 'posting') {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, pt: 8 }}>
        <CircularProgress size={48} />
        <Typography variant="body1" color="text.secondary">Posting transfer…</Typography>
      </Box>
    );
  }

  // ── Success ───────────────────────────────────────────────────────────────
  if (step === 'success') {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5, maxWidth: 560 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 4 }}>
          <PostIcon sx={{ fontSize: 56, color: '#1a7f37' }} />
          <Typography variant="h5" fontWeight={700}>Transfer Posted</Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {movementsPosted} movement{movementsPosted !== 1 ? 's' : ''} posted to the inventory ledger.{' '}
            Stock has been deducted from the source and added to the destination.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="outlined" onClick={() => navigate(entryPath)} sx={{ textTransform: 'none' }}>
            View Posted Transfer
          </Button>
          <Button variant="contained" onClick={() => navigate(listPath)} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Back to Transfers
          </Button>
        </Box>
      </Box>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (step === 'error') {
    return (
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
        <Button
          startIcon={<BackIcon />}
          onClick={() => navigate(entryPath)}
          sx={{ textTransform: 'none', color: 'text.secondary', alignSelf: 'flex-start' }}
        >
          Back to Transfer
        </Button>
        <Alert severity="error">{postError}</Alert>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="outlined" onClick={() => navigate(entryPath)} sx={{ textTransform: 'none' }}>
            Back to Transfer
          </Button>
          <Button variant="contained" onClick={() => setStep('review')} sx={{ textTransform: 'none', fontWeight: 600 }}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  if (!transfer) return null;

  // ── Derived ───────────────────────────────────────────────────────────────
  const missingTransferredAt = !transfer.transferredAt;
  const canPost = lines.length > 0 && !missingTransferredAt;
  const scopeLabel = `${locationLabel(transfer.fromLocationId)} → ${locationLabel(transfer.toLocationId)}`;

  // ── Review render ─────────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* Nav */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(entryPath)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Back to Transfer
          </Button>
          <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
          <Typography variant="h6" fontWeight={700}>Review & Post</Typography>
        </Box>
        <Button
          variant="contained"
          color="success"
          startIcon={<PostIcon />}
          onClick={handlePost}
          disabled={!canPost}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Post Transfer
        </Button>
      </Box>

      {/* Validation warnings */}
      {missingTransferredAt && (
        <Alert severity="warning">
          Transfer date is required before posting. Go back and set the transfer date.
        </Alert>
      )}
      {lines.length === 0 && (
        <Alert severity="warning">
          No lines on this transfer. Add at least one item before posting.
        </Alert>
      )}

      {/* Immutability notice */}
      <Alert severity="warning">
        <strong>Posting is a one-way action.</strong> A <strong>TRANSFER_OUT</strong> and{' '}
        <strong>TRANSFER_IN</strong> movement will be posted to the inventory ledger for each line.
        Stock will be deducted from{' '}
        <strong>{locationLabel(transfer.fromLocationId)}</strong> and added to{' '}
        <strong>{locationLabel(transfer.toLocationId)}</strong>. This cannot be undone.
      </Alert>

      {/* Summary */}
      <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', display: 'flex', gap: 4, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
            Route
          </Typography>
          <Typography variant="body1" fontWeight={600}>{scopeLabel}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
            Lines
          </Typography>
          <Typography variant="h4" fontWeight={700}>{lines.length}</Typography>
        </Box>
        <Box>
          <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase' }}>
            Total Units
          </Typography>
          <Typography variant="h4" fontWeight={700}>
            {lines.reduce((sum, l) => sum + l.quantity, 0)}
          </Typography>
        </Box>
      </Paper>

      {/* Lines table */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
        <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}` }}>
          <Typography variant="subtitle1" fontWeight={600}>Lines to Transfer</Typography>
          <Typography variant="caption" color="text.secondary">
            TRANSFER_OUT and TRANSFER_IN movements will be created for each line
          </Typography>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {['Wine', 'Size', 'From', 'To', 'Qty'].map(h => (
                  <TableCell
                    key={h}
                    sx={{ fontWeight: 600, color: theme.palette.text.secondary, fontSize: '0.7rem', textTransform: 'uppercase', py: 1.25 }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map(line => (
                <TableRow key={line._id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>{line.snapshot.displayName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">{formatBottleSize(line.snapshot.bottleSize)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {line.snapshot.fromLocationName ?? `${venueName} (General)`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {line.snapshot.toLocationName ?? `${venueName} (General)`}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{line.quantity}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Footer actions */}
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button onClick={() => navigate(entryPath)} sx={{ textTransform: 'none' }}>
          Back
        </Button>
        <Button
          variant="contained"
          color="success"
          startIcon={<PostIcon />}
          onClick={handlePost}
          disabled={!canPost}
          sx={{ textTransform: 'none', fontWeight: 600 }}
        >
          Post Transfer
        </Button>
      </Box>
    </Box>
  );
};

export default TransferReview;
